import { useQuery } from '@tanstack/react-query';
import { broadcasts } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Group, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../../i18n/I18nProvider';
import { formatCount } from '../../../../lib/format';
import { RecipientStatusBadge } from '../../components/delivery';

/** The bar under each status, in the same family as its badge. */
const BAR: Record<string, string> = {
  read: 'var(--rr-color-success, #10b981)',
  delivered: 'var(--rr-color-primary, #0d9488)',
  failed: 'var(--rr-color-error, #dc2626)',
  cancelled: 'var(--rr-color-warning, #f59e0b)',
  queued: 'var(--rr-color-info, #3b82f6)'
};

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

export function StatsTab({ broadcastId, poll }: { broadcastId: string; poll: number | false }) {
  const t = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ['broadcast', broadcastId, 'stats'],
    queryFn: () => broadcasts.getBroadcastStats(broadcastId),
    /*
     * §4 item 1, "the most confusing bug on the page": this had no
     * `refetchInterval` and nothing invalidated it, so sitting on the Stats
     * tab of a RUNNING broadcast showed a histogram frozen at mount time while
     * the header, on the same page, ticked upward.
     */
    refetchInterval: poll,
  });

  if (isLoading) {
    return (
      <Card withBorder>
        <Stack gap="xs">
          <Skeleton height={20} />
          <Skeleton height={20} />
          <Skeleton height={20} />
        </Stack>
      </Card>
    );
  }
  if (error) return <Alert color="red">{(error as Error).message}</Alert>;
  const stats = data?.data;
  if (!stats) return <Alert color="warning">{t('broadcasts.view.stats.empty')}</Alert>;

  const total = Math.max(stats.recipients_total, 1);

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('broadcasts.view.stats.title')}</Title>
          <Text fz="xs" c={MUTED}>
            {t('broadcasts.view.stats.hintPlain')}
          </Text>
          {stats.by_status.length === 0 ? (
            <Text fz="sm" c={MUTED}>{t('broadcasts.view.stats.noEntries')}</Text>
          ) : (
            <Stack gap={4}>
              {stats.by_status
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((s) => {
                  const pct = Math.round((s.count / total) * 100);
                  return (
                    <div key={s.code}>
                      <div style={{ marginBottom: 2 }}>
                        <Group justify="space-between" align="baseline">
                        <Group gap="xs" align="baseline">
                          <RecipientStatusBadge code={s.code} t={t} />
                          <Text fz="xs" c={MUTED}>{pct}%</Text>
                        </Group>
                        <Text fz="sm" fw={600}>{formatCount(s.count)}</Text>
                      </Group>
                      </div>
                      <div
                        style={{
                          height: 4,
                          borderRadius: 999,
                          background: 'var(--rr-color-surface-elevated, #1f2937)',
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: BAR[s.code] ?? 'var(--rr-color-text-muted, #94a3b8)'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
